"use client";

import {
  Image as SitecoreImage,
  Link as SitecoreLink,
  RichText,
  Text,
} from "@sitecore-content-sdk/nextjs";
import { type JSX, useEffect, useRef, useState } from "react";

import type { ProgressiveProfilePopupProps } from "./progressive-profile-popup.props";

const TEMP_DISABLE_COOKIE_NAME =
  process.env.NEXT_PUBLIC_PROGRESSIVE_PROFILE_TEMP_DISABLE_COOKIE_NAME ||
  "ProgressiveProfileTempDisablePopup";
const TEMP_DISABLE_DAYS = Number(
  process.env.NEXT_PUBLIC_PROGRESSIVE_PROFILE_TEMP_DISABLE_DAYS || 30,
);

const getCookie = (name: string): string | null => {
  try {
    if (typeof document === "undefined") return null;

    const cookie = document.cookie
      .split("; ")
      .find((item) => item.startsWith(`${name}=`));

    if (!cookie) return null;

    return decodeURIComponent(cookie.split("=").slice(1).join("="));
  } catch (error) {
    console.error("Failed to read cookie", error);
    return null;
  }
};

const setCookie = (name: string, value: string, days: number): void => {
  try {
    if (typeof document === "undefined") return;

    const expires = new Date();
    expires.setDate(expires.getDate() + days);

    document.cookie = `${name}=${encodeURIComponent(
      value,
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error("Failed to set cookie", error);
  }
};

const parseTimeoutToCall = (value?: string | number): number => {
  try {
    if (value === undefined || value === null) return 0;

    const parsedValue = typeof value === "number" ? value : Number(value);

    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      return 0;
    }

    return parsedValue;
  } catch (error) {
    console.error("Failed to parse timeout value", error);
    return 0;
  }
};

const hasTextValue = (value?: string): boolean => {
  try {
    return !!value && value.trim().length > 0;
  } catch (error) {
    console.error("Failed to validate text value", error);
    return false;
  }
};

export const Default = (props: ProgressiveProfilePopupProps): JSX.Element => {
  const fields = props?.fields;
  const isPageEditing = props?.page?.mode?.isEditing ?? false;

  const isProgressiveProfileEnabled = (() => {
    try {
      return (
        (process.env.NEXT_PUBLIC_IS_PROGRESSIVE_PROFILE_ENABLED || "false") ===
        "true"
      );
    } catch (error) {
      console.error("Failed to read progressive profile feature flag", error);
      return false;
    }
  })();

  const [isOpen, setIsOpen] = useState(false);

  const timerRef = useRef<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusedElementRef = useRef<HTMLElement | null>(null);

  const timeoutToCall = parseTimeoutToCall(fields?.TimeoutToCall?.value);

  const hasRequiredContent = (() => {
    try {
      return (
        hasTextValue(fields?.Title?.value) ||
        hasTextValue(fields?.Description?.value) ||
        !!fields?.MainIcon?.value?.src ||
        !!fields?.StartNow?.value?.href
      );
    } catch (error) {
      console.error("Failed to validate popup content", error);
      return false;
    }
  })();

  const closeModal = (): void => {
    try {
      setIsOpen(false);

      window.setTimeout(() => {
        previousFocusedElementRef.current?.focus?.();
      }, 0);
    } catch (error) {
      console.error("Failed to close modal", error);
    }
  };

  const handleNotNowClick = (): void => {
    try {
      setCookie(TEMP_DISABLE_COOKIE_NAME, "true", TEMP_DISABLE_DAYS);
      closeModal();
    } catch (error) {
      console.error("Failed to handle not now click", error);
    }
  };

  useEffect(() => {
    try {
      if (isPageEditing) return;
      if (!isProgressiveProfileEnabled) return;
      if (!hasRequiredContent) return;

      const isTemporarilyDisabled =
        getCookie(TEMP_DISABLE_COOKIE_NAME) === "true";

      if (isTemporarilyDisabled) return;

      timerRef.current = window.setTimeout(() => {
        previousFocusedElementRef.current =
          document.activeElement as HTMLElement;
        setIsOpen(true);
      }, timeoutToCall);

      return () => {
        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
        }
      };
    } catch (error) {
      console.error("Failed to initialize popup timer", error);
    }
  }, [
    isPageEditing,
    isProgressiveProfileEnabled,
    hasRequiredContent,
    timeoutToCall,
  ]);

  useEffect(() => {
    try {
      if (!isOpen) return;

      const previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      window.setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);

      const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
          closeModal();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.removeEventListener("keydown", handleKeyDown);
      };
    } catch (error) {
      console.error("Failed to manage modal keyboard behavior", error);
    }
  }, [isOpen]);

  if (isPageEditing) {
    return (
      <div className="rounded border border-dashed border-blue-500 bg-blue-50 p-3 text-sm font-semibold text-blue-700">
        Progressive Profile Popup
      </div>
    );
  }

  if (!isProgressiveProfileEnabled || !hasRequiredContent || !isOpen) {
    return <></>;
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 px-6 py-8 max-md:items-start max-md:px-[18px] max-md:pt-[128px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="progressive-profile-title"
        aria-describedby="progressive-profile-description"
        className="relative w-full max-w-[620px] border-t-[5px] border-t-[#0072ce] bg-white shadow-[0_12px_32px_rgba(0,0,0,0.3)] max-md:max-w-[390px]"
      >
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close"
          onClick={closeModal}
          className="absolute right-[14px] top-[10px] z-[1] h-8 w-8 border-0 bg-transparent p-0 text-[34px] font-bold leading-[28px] text-[#333] hover:text-black focus:text-black max-md:right-[10px] max-md:top-2"
        >
          ×
        </button>

        <div className="flex min-h-[235px] items-center gap-[42px] px-[54px] pb-[42px] pt-[54px] max-md:min-h-0 max-md:flex-col max-md:items-start max-md:gap-[22px] max-md:px-[26px] max-md:pb-10 max-md:pt-[38px]">
          {fields?.MainIcon?.value?.src && (
            <div className="flex w-[112px] shrink-0 items-center justify-center max-md:w-full">
              <SitecoreImage
                field={fields.MainIcon}
                className="block h-auto max-w-[112px] max-md:max-w-[104px]"
              />
            </div>
          )}

          <div className="min-w-0 flex-1">
            {fields?.Title && (
              <h2
                id="progressive-profile-title"
                className="mb-2 mt-0 text-[26px] font-bold leading-[1.14] text-black max-md:mb-[14px] max-md:text-[25px] max-md:leading-[1.17]"
              >
                <Text field={fields.Title} />
              </h2>
            )}

            {fields?.Description && (
              <div
                id="progressive-profile-description"
                className="text-[21px] font-normal leading-[1.24] text-black [&_a]:text-[#005eb8] [&_a]:underline [&_p]:m-0 [&_p+p]:mt-3 max-md:text-[22px] max-md:leading-[1.12]"
              >
                <RichText field={fields.Description} />
              </div>
            )}
          </div>
        </div>

        <div className="flex min-h-[88px] items-center justify-center gap-[42px] bg-[#0072ce] px-8 py-5 max-md:min-h-[123px] max-md:flex-col-reverse max-md:gap-[14px] max-md:px-6 max-md:pb-6 max-md:pt-5">
          {fields?.NotNowText && (
            <button
              type="button"
              onClick={handleNotNowClick}
              className="border-0 bg-transparent text-base font-bold leading-[1.2] text-white underline hover:text-white hover:decoration-2 focus:text-white focus:decoration-2"
            >
              <Text field={fields.NotNowText} />
            </button>
          )}

          {fields?.StartNow?.value?.href && (
            <SitecoreLink
              field={fields.StartNow}
              className="inline-flex min-h-[42px] min-w-[94px] items-center justify-center rounded-full bg-white px-5 py-[10px] text-center text-base font-bold leading-[1.2] text-[#0072ce] underline hover:bg-white hover:text-[#005eb8] hover:decoration-2 focus:bg-white focus:text-[#005eb8] focus:decoration-2 max-md:min-h-[47px] max-md:min-w-[98px] max-md:px-[22px] max-md:py-3"
            >
              {fields?.StartNowText?.value || fields.StartNow.value.text}
            </SitecoreLink>
          )}
        </div>
      </section>
    </div>
  );
};